import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { CheckCircle2, XCircle, Loader2, Mail, FileText } from "lucide-react";
import PublicLayout from "@/components/layout/PublicLayout";
import { formatMoney } from "@/lib/format";

export default function PaySuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ loading: true });

  useEffect(() => {
    if (!sessionId) {
      setState({ loading: false, error: "No payment session reference." });
      return;
    }

    let attempts = 0;
    const max = 10;
    let active = true;

    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await axios.get(`${API}/payments/checkout/status/${sessionId}`);
        if (!active) return;
        if (data.payment_status === "paid") {
          setState({ loading: false, paid: true, data });
          return;
        }
        if (data.status === "expired") {
          setState({ loading: false, error: "Payment session expired." });
          return;
        }
        if (attempts < max) {
          setTimeout(poll, 2500);
        } else {
          setState({ loading: false, pending: true, data });
        }
      } catch (e) {
        if (!active) return;
        if (attempts < max) setTimeout(poll, 2500);
        else setState({ loading: false, error: "Couldn't verify payment status. Please contact info@bdtt.se with your session id." });
      }
    };
    poll();
    return () => { active = false; };
  }, [sessionId]);

  return (
    <PublicLayout>
      <section className="relative" data-testid="pay-success-page">
        <div className="bg-stage" />
        <div className="absolute inset-0 pointer-events-none">
          <div className="bg-grid" />
          <div className="orb orb-green" />
        </div>
        <div className="relative z-10 max-w-[700px] mx-auto px-5 sm:px-8 lg:px-12 pt-20 pb-24 text-center">
          {state.loading && (
            <div className="flex flex-col items-center gap-4" data-testid="pay-status-loading">
              <Loader2 className="w-10 h-10 animate-spin text-[color:var(--bd-green-bright)]" />
              <div className="font-display text-2xl">Confirming your payment…</div>
              <p className="text-[color:var(--ivory-muted)] text-sm">Please don't close this window.</p>
            </div>
          )}

          {!state.loading && state.paid && (
            <div className="space-y-6" data-testid="pay-status-paid">
              <div className="w-20 h-20 mx-auto rounded-full bg-[color:var(--bd-green)]/20 flex items-center justify-center">
                <CheckCircle2 className="w-10 h-10 text-[color:var(--bd-green-bright)]" />
              </div>
              <h1 className="font-display text-4xl sm:text-5xl">Thank you!</h1>
              <p className="text-[color:var(--ivory)]">
                Your payment of <span className="font-semibold text-[color:var(--bd-green-bright)]">{formatMoney((state.data?.amount_total || 0) / 100, "SEK")}</span> has been received.
              </p>
              {state.data?.metadata?.description && (
                <p className="text-sm text-[color:var(--ivory-muted)]">
                  For: <span className="text-[color:var(--ivory)]">{state.data.metadata.description}</span>
                </p>
              )}
              <p className="text-sm text-[color:var(--ivory-muted)] max-w-md mx-auto">
                A confirmation email from Stripe is on its way. We'll be in touch shortly. If you have any questions, just reply to that email or contact us.
              </p>
              <div className="flex flex-wrap gap-3 justify-center pt-4">
                <Link to="/" className="pill pill-primary">Back to home</Link>
                <a href="mailto:info@bdtt.se" className="pill border border-white/15 text-white inline-flex items-center gap-2"><Mail className="w-4 h-4" /> info@bdtt.se</a>
              </div>
            </div>
          )}

          {!state.loading && state.pending && (
            <div className="space-y-4" data-testid="pay-status-pending">
              <Loader2 className="w-10 h-10 mx-auto animate-spin text-[color:var(--ivory-muted)]" />
              <h1 className="font-display text-3xl">Almost done…</h1>
              <p className="text-[color:var(--ivory-muted)] text-sm">
                Stripe is still processing your payment. You'll receive a confirmation email shortly.
              </p>
              <Link to="/" className="pill border border-white/15 text-white inline-flex items-center gap-2 mt-3">Back to home</Link>
            </div>
          )}

          {!state.loading && state.error && (
            <div className="space-y-4" data-testid="pay-status-error">
              <div className="w-20 h-20 mx-auto rounded-full bg-[color:var(--bd-red)]/15 flex items-center justify-center">
                <XCircle className="w-10 h-10 text-[color:var(--bd-red-bright)]" />
              </div>
              <h1 className="font-display text-3xl">Couldn't confirm</h1>
              <p className="text-[color:var(--ivory-muted)]">{state.error}</p>
              <div className="flex flex-wrap gap-3 justify-center pt-3">
                <Link to="/pay" className="pill pill-primary inline-flex items-center gap-2"><FileText className="w-4 h-4" /> Try again</Link>
                <a href="mailto:info@bdtt.se" className="pill border border-white/15 text-white">Email us</a>
              </div>
            </div>
          )}
        </div>
      </section>
    </PublicLayout>
  );
}
